import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import { ManagedChart, ManagedChartDataSet } from '../Shared/ManagedChart';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminOperation,
    ManagedAdminEditorMode,
    ManagedActionModalMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    mapChartAdminModel,
    mapSlimDataSetAdminModel,
    SlimDataSetAdminModel,
 } from '../Shared/ManagedHelpers';
import Administration from '../../Constants/Endpoints/Administration';
import GetChartAdminModelsWithChildrenRequest from '../../Interfaces/Requests/Administration/GetChartAdminModelsWithChildrenRequest';
import GetChartAdminModelsWithChildrenResponse from '../../Interfaces/Responses/Administration/GetChartAdminModelsWithChildrenResponse';
import AUDChartRequest from '../../Interfaces/Requests/Administration/AUDChartRequest';
import {
    AddIcon,
    Button,
    Card,
    Checkbox,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Modal as ServaModal,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import { CloseCircleOutlined } from '@ant-design/icons';
import BulbOutlinedIlluminated from '../Shared/BulbOutlinedIlluminated';
import AIContextAssistModal from '../Shared/AIContextAssistModal';
import GetDataSetAdminModelsRequest from '../../Interfaces/Requests/Administration/GetDataSetAdminModelsRequest';
import GetDataSetAdminModelsResponse from '../../Interfaces/Responses/Administration/GetDataSetAdminModelResponse';
import GetAIProviderListModelRequest from '../../Interfaces/Requests/AI/GetAIProviderListModelRequest';
import GetAIChartExplainModelRequest from '../../Interfaces/Requests/AI/GetAIChartExplainModelRequest';
import GetAIProviderListModelResponse from '../../Interfaces/Responses/AI/GetAIProviderListModelResponse';
import GetAIExplainModelResponse from '../../Interfaces/Responses/AI/GetAIExplainModelResponse';
import AUDChartDataSet, { ManagedChartDataSetEditResult } from './AUDChartDataSet';

type AUDChartProps = {};

type AUDChartAIContext = {
    title: string;
    currentSectionTitle: string;
    requestSectionTitle: string;
    resultSectionTitle: string;
    requestPlaceholder: string;
    askButtonText: string;
    useButtonText: string;
    providerNamePhrase: string;
    targetTextDescription: string;
    allowUseSuggestion: boolean;
    assistMode: 'chartExplain';
};

const AUDChart: React.FC<AUDChartProps> = () => {
    const [formAUDCharts] = Form.useForm();

    const [manageActionModalState, setManageActionModalState] = useState<ManagedActionModalState>({ open: false, mode: 'info', title: '', message: '', isError: false, });

    const [addButtonClicked, setAddButtonClicked] = useState(true);
    const [updButtonClicked, setUpdButtonClicked] = useState(false);
    const [delButtonClicked, setDelButtonClicked] = useState(false);

    const [cSelectedChartID, setCSelectedChartID] = useState(-1);
    const [cSelectedChartTitle, setCSelectedChartTitle] = useState('');

    const [cSelectedChartDataSetID, setCSelectedChartDataSetID] = useState(-1);
    const [cChartDataSetEditorMode, setCChartDataSetEditorMode] = useState<ManagedAdminActionMode>('idle');

    const [cDetailsChartDataSet, setCDetailsChartDataSet] = useState<ManagedChartDataSet[]>([]);

    const [manageChartDataSetModalOpen, setManageChartDataSetModalOpen] = useState(false);

    const [cIncludeDisabled] = useState(false);
    const [dsIncludeDisabled] = useState(false);

    const [aiContextAssistModalOpen, setAIContextAssistModalOpen] = useState(false);
    const [aiContextAssistCurrentText, setAIContextAssistCurrentText] = useState('');
    const [aiContextAssistResultText, setAIContextAssistResultText] = useState('');
    const [aiContextAssistAncillaryItems, setAIContextAssistAncillaryItems] = useState<string[]>([]);
    const [aiContextAssistLoading, setAIContextAssistLoading] = useState(false);

    const [aiContextAssistContext, setAIContextAssistContext] = useState<AUDChartAIContext>({
        title: 'AI Chart Explain',
        currentSectionTitle: 'Current Chart Selection',
        requestSectionTitle: 'What do you want to understand about this Chart?',
        resultSectionTitle: 'Chart Explanation',
        requestPlaceholder: 'Ask AI to explain the selected Chart, its backing DataSet, fields, formatting, or drilldowns.',
        askButtonText: 'Ask AI',
        useButtonText: 'Use Suggestion',
        providerNamePhrase: 'Chart Explain',
        targetTextDescription: 'Chart explanation',
        allowUseSuggestion: false,
        assistMode: 'chartExplain',
    });
    
    const showCInfoModal = (title: string, message: string, isError: boolean = true) => {
        setManageActionModalState(buildManagedActionModalState('info', title, message, isError));
    };

    const buildCErrorMessage = (prefix: string, err: unknown): string => {
        const apiError = err as ManagedApiError;

        return `${prefix}${apiError?.response?.data?.message ? ` ${apiError.response.data.message}` : ''}`;
    };

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredCField = (fieldName: string): Promise<void> => {
        const value = formAUDCharts.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateCDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'cDetailsRefreshPeriod':
            case 'cDetailsTitle':
            case 'cDetailsDataPointLabel':
                return validateRequiredCField(rule.field);

            case 'cDetailsDataSetID':
            case 'cDetailsTitleGeneric':
            case 'cDetailsShowLegend':
            case 'cDetailsXAxisTitle':
            case 'cDetailsYAxisTitle':
            case 'cDetailsOpacity':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const setCMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const getCOperation = (): ManagedAdminOperation => {
        if (addButtonClicked) return 'Add';
        if (updButtonClicked) return 'Upd';
        if (delButtonClicked) return 'Del';
        return '';
    };

    const getCActionModalMode = (): ManagedActionModalMode => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        if (delButtonClicked) return 'delete';
        return 'info';
    };

    const getCActionLabel = (): string => {
        if (addButtonClicked) return 'Add';
        if (updButtonClicked) return 'Update';
        if (delButtonClicked) return 'Delete';
        return 'Manage';
    };


    const getCActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const getCActionTargetName = (): string => {
        return cSelectedChartTitle || formAUDCharts.getFieldValue('cDetailsTitle') || '';
    };

    const showCResultModal = (isError: boolean, message: string) => {
        setManageActionModalState(buildManagedActionModalState(getCActionModalMode(), `${getCActionLabel()} Chart`, message, isError));
    };

    const clearCSelection = () => {
        setCSelectedChartID(-1);
        setCSelectedChartTitle('');
        setCMode('add');
        setManageChartDataSetModalOpen(false);
        setCBlankForm();
    };

    const buildAUDChartRequest = (): AUDChartRequest => {
        const cDetailsRefreshPeriod = Number(formAUDCharts.getFieldValue('cDetailsRefreshPeriod'));
        const cDetailsDataSetID = Number(formAUDCharts.getFieldValue('cDetailsDataSetID'));
        const cDetailsTitle = formAUDCharts.getFieldValue('cDetailsTitle');
        const cDetailsShowLegend = formAUDCharts.getFieldValue('cDetailsShowLegend');
        const cDetailsDataPointLabel = formAUDCharts.getFieldValue('cDetailsDataPointLabel');
        const cDetailsTitleGeneric = formAUDCharts.getFieldValue('cDetailsTitleGeneric');
        const cDetailsOpacity = Number(formAUDCharts.getFieldValue('cDetailsOpacity'));
        const cDetailsYAxisTitle = formAUDCharts.getFieldValue('cDetailsYAxisTitle');
        const cDetailsXAxisTitle = formAUDCharts.getFieldValue('cDetailsXAxisTitle');

        return {
            id: cSelectedChartID,
            refreshPeriod: Number.isFinite(cDetailsRefreshPeriod) ? cDetailsRefreshPeriod : 0,
            dataSetId: Number.isFinite(cDetailsDataSetID) ? cDetailsDataSetID : 0,
            title: cDetailsTitle ?? '',
            showLegend: cDetailsShowLegend ?? false,
            dataPoint_Label: cDetailsDataPointLabel ?? '',
            titleGeneric: cDetailsTitleGeneric ?? '',
            opacity: Number.isFinite(cDetailsOpacity) ? cDetailsOpacity : 0,
            yAxis_Title: cDetailsYAxisTitle ?? '',
            xAxis_Title: cDetailsXAxisTitle ?? '',
            audChartDataSets: cDetailsChartDataSet.map(
                (eachChartDataSetElement) => {
                    const manualAxisMin = eachChartDataSetElement?.manualAxisMin ?? 0;
                    const manualAxisMax = eachChartDataSetElement?.manualAxisMax ?? 0;
                    const useNullManualAxis = manualAxisMin === 0 && manualAxisMax === 0;
                    return {
                        id: eachChartDataSetElement?.id ?? 0,
                        chartId: cSelectedChartID,
                        chartTypeId: eachChartDataSetElement?.chartTypeId ?? 2,
                        axis: eachChartDataSetElement?.axis ?? 'X',
                        label: eachChartDataSetElement?.label ?? '',
                        formatting: eachChartDataSetElement?.formatting ?? '',
                        dataColumn: eachChartDataSetElement?.dataColumn ?? '',
                        reversed: eachChartDataSetElement?.reversed ?? false,
                        alternate_DS_Id: eachChartDataSetElement?.alternateDSId,
                        alternate_DS_X_Column: eachChartDataSetElement?.alternateDSXColumn ?? '',
                        tooltip_Source: eachChartDataSetElement?.tooltipSource ?? '',
                        intervalType: eachChartDataSetElement?.intervalType ?? '',
                        groupBySplit: eachChartDataSetElement?.groupBySplit ?? '',
                        groupByDefaultShow: eachChartDataSetElement?.groupByDefaultShow ?? '',
                        tooltip_Custom: eachChartDataSetElement?.tooltipCustom ?? '',
                        manual_Axis_Min: useNullManualAxis ? null : manualAxisMin,
                        manual_Axis_Max: useNullManualAxis ? null : manualAxisMax,
                        seriesColor: eachChartDataSetElement?.seriesColor ?? '0,0,0',

                        audDrilldowns: eachChartDataSetElement.drilldowns.map((eachDrilldown) => {
                            return {
                                id: eachDrilldown.id,
                                source_DataSet_ID: eachDrilldown.sourceDataSetId,
                                source_Chart_DataSet_ID: eachDrilldown.sourceChartDataSetId,
                                source_Value: eachDrilldown.sourceValue,
                                target_DataSet_ID: eachDrilldown.targetDataSetId,
                                target_Chart_ID: eachDrilldown.targetChartId,
                                target_DataSet_Input_ID: eachDrilldown.targetDataSetInputId,
                                audDrilldownInputPassthroughMaps: eachDrilldown.drilldownInputPassthroughMaps.map((eachPassthroughMap) => {
                                    return {
                                        id: eachPassthroughMap.id,
                                        drilldown_ID: eachPassthroughMap.drilldownId,
                                        source_DataSet_Input_ID: eachPassthroughMap.sourceDataSetInputId,
                                        target_DataSet_Input_ID: eachPassthroughMap.targetDataSetInputId,
                                        operation: eachPassthroughMap.id > 0 ? 'Upd' : 'Add',
                                    };
                                }),
                                operation: eachDrilldown.id > 0 ? 'Upd' : 'Add',
                            };
                        }),

                        syncDrilldowns: true,
                    };
                }
            ),
            operation: getCOperation(),
        };
    };

    const { data: dataSets = [], isLoading: isGetDataSetsLoading } = useQuery<GetDataSetAdminModelsResponse[], Error, SlimDataSetAdminModel[]>(
        ['dataSetAdminModels', dsIncludeDisabled],
        async (): Promise<GetDataSetAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_DATASET_ADMIN_MODELS}`;
                const requestData: GetDataSetAdminModelsRequest = { gdsa_includeDisabled: dsIncludeDisabled };
                return await AxiosUtil.get<GetDataSetAdminModelsResponse[], GetDataSetAdminModelsRequest>(url, requestData);
            } catch (err) {
                console.error('GET_DATASET_ADMIN_MODELS: [Get DataSet Admin Models] Internal Error: ', err);
                throw err;
            }
        },
        {
            onError: (err: Error) => {
                console.error('GET_DATASET_ADMIN_MODELS: [Get DataSet Admin Models] Internal Error: ', err);
                showCInfoModal('Chart Load Error', buildCErrorMessage('Unable to load DataSet choices for Manage Charts.', err));
            },
            select: (response: GetDataSetAdminModelsResponse[]): SlimDataSetAdminModel[] =>
                response
                    .filter((eachDataSet) => !eachDataSet.deleted)
                    .map(mapSlimDataSetAdminModel)
                    .sort((a, b) => a.name.localeCompare(b.name)),
        },
    );

    const { data: aiProviders = [], isLoading: isGetAIProvidersLoading } = useQuery<GetAIProviderListModelResponse[], unknown>(
        ['aud-chart-ai-providers'],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpGet_GetAIProviders`;
                const requestData: GetAIProviderListModelRequest = {};

                return await AxiosUtil.get<GetAIProviderListModelResponse[], GetAIProviderListModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_CHART_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                console.error('AUD_CHART_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                setManageActionModalState(
                    buildManagedActionModalState(
                        'info',
                        'AI Provider Load Error',
                        'Unable to load AI Providers for Chart Explain.',
                        true
                    )
                );
            },
        }
    );

    const getAIContextAssistProvider = (): GetAIProviderListModelResponse | undefined => {
        const providerNamePhrase = aiContextAssistContext.providerNamePhrase.toLowerCase();

        return aiProviders.find((provider) =>
            provider.name.toLowerCase().includes(providerNamePhrase) ||
            provider.displayName.toLowerCase().includes(providerNamePhrase)
        );
    };

    const { mutate: getAIChartExplain } = useMutation(
        async ({ externalVendorID, tenantID, chartID, prompt }: GetAIChartExplainModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpPost_AIChartExplain`;
                const requestData: GetAIChartExplainModelRequest = {
                    externalVendorID: externalVendorID,
                    tenantID: tenantID,
                    chartID: chartID,
                    prompt: prompt,
                };

                return await AxiosUtil.post<GetAIExplainModelResponse, GetAIChartExplainModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_CHART_AI_EXPLAIN: [AI Chart Explain] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAIExplainModelResponse) => {
                setAIContextAssistResultText((response.text ?? '').trim());
                setAIContextAssistAncillaryItems([
                    response.success ? 'AI Chart Explain returned successfully.' : 'AI Chart Explain request did not succeed.',
                    response.message ?? '',
                ].filter((item) => item.trim().length > 0));
                setAIContextAssistLoading(false);
            },
            onError: (err: unknown) => {
                console.error('AUD_CHART_AI_EXPLAIN: [AI Chart Explain] Internal Error: ', err);
                setAIContextAssistAncillaryItems([
                    'AI Chart Explain request failed. Please contact application support if the issue continues.',
                ]);
                setAIContextAssistLoading(false);
            },
        }
    );

    const { data: charts = [], isLoading: isGetChartsLoading, refetch: refetchCharts } = useQuery<GetChartAdminModelsWithChildrenResponse[], Error, ManagedChart[]>(
        ['chartAdminModelsWithChildren', cIncludeDisabled],
        async (): Promise<GetChartAdminModelsWithChildrenResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_CHART_ADMIN_MODELS_WITH_CHILDREN}`;
                const requestData: GetChartAdminModelsWithChildrenRequest = { gcac_includeDisabled: cIncludeDisabled };
                return await AxiosUtil.get<GetChartAdminModelsWithChildrenResponse[], GetChartAdminModelsWithChildrenRequest>(url, requestData);
            } catch (err) {
                console.error('GET_CHART_ADMIN_MODELS: [Get Chart Admin Models AUD] Internal Error: ', err);
                throw err;
            }
        },
        {
            onError: (err: Error) => {
                console.error('GET_CHART_ADMIN_MODELS: [Get Chart Admin Models AUD] Internal Error: ', err);
                showCInfoModal('Chart Load Error', buildCErrorMessage('Unable to load Charts for Manage Charts.', err));
            },
            select: (response: GetChartAdminModelsWithChildrenResponse[]): ManagedChart[] =>
                response
                    .map(mapChartAdminModel)
                    .sort((a, b) => a.title.localeCompare(b.title)),
        },
    );

    const { mutate: audChart, isLoading: isAUDChartLoading, reset: resetAUDChart } = useMutation<boolean, Error, void>(
        async (): Promise<boolean> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.AUD_CHART_DETAILS_BY_ID}`;
                const requestData: AUDChartRequest = buildAUDChartRequest();
                const responseData = await AxiosUtil.post<boolean, AUDChartRequest>(url, requestData);
                return responseData;
            } catch (err) {
                console.error('AUD_CHART_DETAILS_BY_ID: [AUD Chart t/c] Internal Error: ', err);
                throw err;
            }
        },
        {
            onError: (err: Error) => {
                const chartTitle = getCActionTargetName();
                console.error('AUD_CHART_DETAILS_BY_ID: [AUD Chart o/e] Internal Error: ', err);
                showCResultModal(true, `${getCActionLabel()} Chart: ${chartTitle} Failed. Please Contact Application Support`);
            },
            onSuccess: async (response: boolean) => {
                const chartTitle = getCActionTargetName();
                const actionLabel = getCActionLabel();

                if (delButtonClicked) {
                    clearCSelection();
                    await refetchCharts();
                    showCResultModal(false, `${actionLabel} Chart: ${chartTitle} Succeeded.`);
                    return;
                }

                const refetchResult = await refetchCharts();
                const refreshedChart = refetchResult.data?.find((eachChart) => eachChart.id === cSelectedChartID);

                if (refreshedChart) {
                    setCFormFromSelectedChart(refreshedChart);
                }

                showCResultModal(false, `${actionLabel} Chart: ${chartTitle} Succeeded.`);
            },
        }
    );

    const getChartDataSetEditorContext = () => {
        const chartTitle = formAUDCharts.getFieldValue('cDetailsTitle') ?? cSelectedChartTitle ?? '';

        return {
            chartID: cSelectedChartID > 0 ? cSelectedChartID : 0,
            chartTitle,
            chartDataSets: cDetailsChartDataSet,
            initialSelectedChartDataSetID: cSelectedChartDataSetID,
            initialMode: cChartDataSetEditorMode,
        };
    };

    const openCChartDataSetEditor = (
        mode: ManagedAdminChildOperation,
        selectedChartDataSetID: number = -1
    ) => {
        setCChartDataSetEditorMode(mode);
        setCSelectedChartDataSetID(selectedChartDataSetID);
        setManageChartDataSetModalOpen(true);
    };

    const closeCChartDataSetEditor = () => {
        setManageChartDataSetModalOpen(false);
        setCChartDataSetEditorMode('idle');
        setCSelectedChartDataSetID(-1);
    };

    const onCDataSet = () => {
        openCChartDataSetEditor('add');
    }

    const applyChartDataSetEditResult = (
        existingChartDataSets: ManagedChartDataSet[],
        result: ManagedChartDataSetEditResult
    ): ManagedChartDataSet[] => {
        const { operation, chartDataSet } = result;

        switch (operation) {
            case 'add':
                return [...existingChartDataSets, chartDataSet];

            case 'update':
                return existingChartDataSets.map((eachChartDataSet) =>
                    eachChartDataSet.clientKey === chartDataSet.clientKey ? chartDataSet : eachChartDataSet
                );

            case 'delete':
                return existingChartDataSets.filter(
                    (eachChartDataSet) => eachChartDataSet.clientKey !== chartDataSet.clientKey
                );

            default:
                return existingChartDataSets;
        }
    };

    const handleSetChartDataSet = (result: ManagedChartDataSetEditResult) => {
        setCDetailsChartDataSet((currentChartDataSets) =>
            applyChartDataSetEditResult(currentChartDataSets, result)
        );
    };

    const onFinishFailed = (_errorInfo: ManagedFormErrorInfo) => {
        showCInfoModal('Chart Validation Error', 'Please correct the highlighted Chart fields and try again.');
    };

    const onFinish = () => {
        audChart();
    }

    const getSelectedChart = (selectedChartID: number = cSelectedChartID): ManagedChart | undefined => {
        return charts.find((chart) => chart.id === selectedChartID);
    };

    const buildChartExplainSelectionSummary = (chart: ManagedChart | undefined): string => {
        if (!chart) {
            return 'No Chart selected.';
        }

        const backingDataSet = dataSets.find((dataSet) => dataSet.id === chart.dataSetId);

        return [
            `Chart ID: ${chart.id}`,
            `Chart Title: ${chart.title}`,
            `Generic Title: ${chart.titleGeneric ?? ''}`,
            `Backing DataSet ID: ${chart.dataSetId}`,
            `Backing DataSet Name: ${backingDataSet?.name ?? ''}`,
            `Chart DataSet Count: ${chart.chartDataSets.length}`,
            '',
            'The detailed Chart Explain context is built by the backend.',
        ].join('\n');
    };

    const handleOpenAIChartExplainModal = () => {
        const selectedChart = getSelectedChart();

        setAIContextAssistContext({
            title: 'AI Chart Explain',
            currentSectionTitle: 'Current Chart Selection',
            requestSectionTitle: 'What do you want explained?',
            resultSectionTitle: 'Chart Explanation',
            requestPlaceholder: 'Optionally describe what part of this Chart you want explained.',
            askButtonText: 'Explain Chart',
            useButtonText: 'Use Suggestion',
            providerNamePhrase: 'Chart Explain',
            targetTextDescription: 'plain-language Chart explanation',
            allowUseSuggestion: false,
            assistMode: 'chartExplain',
        });

        setAIContextAssistCurrentText(buildChartExplainSelectionSummary(selectedChart));
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);
        setAIContextAssistModalOpen(true);
    };

    const handleAskAIContextAssist = (requestText: string) => {
        const aiContextAssistProvider = getAIContextAssistProvider();

        if (!aiContextAssistProvider) {
            setAIContextAssistAncillaryItems([
                `Unable to find an AI Provider matching ${aiContextAssistContext.providerNamePhrase}.`,
            ]);
            return;
        }

        setAIContextAssistLoading(true);
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);

        getAIChartExplain({
            externalVendorID: aiContextAssistProvider.externalVendorID,
            tenantID: null,
            chartID: cSelectedChartID,
            prompt: requestText,
        });
    };

    const handleCopyAIContextAssistResult = (resultText: string) => {
        navigator.clipboard.writeText(resultText);
    };

    const handleCSelectionChange = (value: number) => {
        setCSelectedChartID(value);

        const selectedChart = getSelectedChart(value);

        setCSelectedChartTitle(selectedChart?.title ?? '');

        setCMode('add');
        setCBlankForm();
    };

    const setCBlankForm = () => {
        formAUDCharts.setFieldValue('cDetailsRefreshPeriod', 0);
        formAUDCharts.setFieldValue('cDetailsDataSetID', undefined);
        formAUDCharts.setFieldValue('cDetailsTitle', '');
        formAUDCharts.setFieldValue('cDetailsShowLegend', false);
        formAUDCharts.setFieldValue('cDetailsDataPointLabel', '');
        formAUDCharts.setFieldValue('cDetailsTitleGeneric', '');
        formAUDCharts.setFieldValue('cDetailsOpacity', 0);
        formAUDCharts.setFieldValue('cDetailsYAxisTitle', '');
        formAUDCharts.setFieldValue('cDetailsXAxisTitle', '');

        setCDetailsChartDataSet([]);
    };

    const setCFormFromSelectedChart = (chartOverride?: ManagedChart) => {
        const selectedChart = chartOverride ?? getSelectedChart();

        formAUDCharts.setFieldValue('cDetailsRefreshPeriod', selectedChart?.refreshPeriod ?? 0);
        formAUDCharts.setFieldValue('cDetailsDataSetID', selectedChart?.dataSetId ?? 0);
        formAUDCharts.setFieldValue('cDetailsTitle', selectedChart?.title ?? '');
        formAUDCharts.setFieldValue('cDetailsShowLegend', selectedChart?.showLegend ?? false);
        formAUDCharts.setFieldValue('cDetailsDataPointLabel', selectedChart?.dataPointLabel ?? '');
        formAUDCharts.setFieldValue('cDetailsTitleGeneric', selectedChart?.titleGeneric ?? '');
        formAUDCharts.setFieldValue('cDetailsOpacity', selectedChart?.opacity ?? 0);
        formAUDCharts.setFieldValue('cDetailsYAxisTitle', selectedChart?.yAxisTitle ?? '');
        formAUDCharts.setFieldValue('cDetailsXAxisTitle', selectedChart?.xAxisTitle ?? '');

        setCDetailsChartDataSet(selectedChart?.chartDataSets ?? []);
    };

    const handleCChartResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setCBlankForm();
            return;
        }

        setCFormFromSelectedChart();
    };

    //=================================================================
    //=================================================================
    
    const handleCAddChart = () => {
        resetAUDChart();
        clearCSelection();
    };

    //=================================================================
    //=================================================================

    const handleCUpdChart = () => {
        resetAUDChart();
        setCMode('update');
        handleCChartResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleCDelChart = () => {
        resetAUDChart();
        setCMode('delete');
        handleCChartResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleManageModalOk = () => {
        setManageActionModalState((prev) => ({
            ...prev,
            open: false,
        }));
    };

    const onCCancel = () => {
        resetAUDChart();
        clearCSelection();
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '8px',
        height: '470px'
    };
    
    return (
        <Card className='shadow-card admin-full-page-card' size='small'>
            <ManagedActionModal
                modalState={manageActionModalState}
                onOk={handleManageModalOk}
            />
            <AIContextAssistModal
                open={aiContextAssistModalOpen}
                title={aiContextAssistContext.title}
                currentSectionTitle={aiContextAssistContext.currentSectionTitle}
                requestSectionTitle={aiContextAssistContext.requestSectionTitle}
                resultSectionTitle={aiContextAssistContext.resultSectionTitle}
                ancillarySectionTitle='Ancillary Information'
                currentText={aiContextAssistCurrentText}
                resultText={aiContextAssistResultText}
                ancillaryItems={aiContextAssistAncillaryItems}
                requestPlaceholder={aiContextAssistContext.requestPlaceholder}
                askButtonText={aiContextAssistContext.askButtonText}
                useButtonText={aiContextAssistContext.useButtonText}
                loading={aiContextAssistLoading || isGetAIProvidersLoading}
                allowUseSuggestion={aiContextAssistContext.allowUseSuggestion}
                onAskAI={handleAskAIContextAssist}
                onCopy={handleCopyAIContextAssistResult}
                onUseSuggestion={() => { }}
                onClose={() => setAIContextAssistModalOpen(false)}
            />
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='60%'
                open={manageChartDataSetModalOpen}
                onCancel={closeCChartDataSetEditor}
            >
                {
                    manageChartDataSetModalOpen &&
                    (
                        <AUDChartDataSet
                            chartID={getChartDataSetEditorContext().chartID}
                            chartTitle={getChartDataSetEditorContext().chartTitle}
                            chartDataSets={getChartDataSetEditorContext().chartDataSets}
                            initialSelectedChartDataSetID={getChartDataSetEditorContext().initialSelectedChartDataSetID}
                            initialMode={getChartDataSetEditorContext().initialMode}
                            handleSetChartDataSet={handleSetChartDataSet}
                            close={closeCChartDataSetEditor}
                        />
                    )
                }
            </ServaModal>
            <Spin spinning={
                            isGetDataSetsLoading ||
                            isGetChartsLoading ||
                            isAUDChartLoading ||
                            isGetAIProvidersLoading
                           }>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Charts</Title>
                    </Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{ float: 'right' }}>Select Chart to Update or Delete:</Text>
                    </Col>
                    <Col span={10}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a Chart'}
                            showSearch
                            style={{paddingTop: '4px', width: '100%'}}
                            value={cSelectedChartID === -1 ? undefined : cSelectedChartID}
                            onChange={handleCSelectionChange}
                        >
                            {charts.map(
                                (eachChart) => {
                                    return (
                                        <Option
                                            key={eachChart.id}
                                            value={eachChart.id}
                                            label={eachChart.title}
                                        >
                                            {eachChart.title}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                    <Col span={1}>
                        <Button
                            disabled={cSelectedChartID === -1 || isGetAIProvidersLoading}
                            icon={<BulbOutlinedIlluminated />}
                            style={{minWidth: '32px', padding: '2px',}}
                            title='AI Chart Explain'
                            type='default'
                            onClick={handleOpenAIChartExplainModal}
                        />
                    </Col>
                    <Col span={3} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={3}>
                        <Button
                            icon={<AddIcon />}
                            style={{width: '95%', marginBottom: '4px', float: 'left',}}
                            type='primary'
                            onClick={handleCAddChart}
                        >
                            Add Chart
                        </Button>
                    </Col>
                    <Col span={9} />
                    <Col span={3}>
                        <Button
                            disabled={cSelectedChartID === -1}
                            icon={<EditIcon />}
                            style={{width: '95%', backgroundColor: 'darkgray', float: 'right', marginBottom: '4px',}}
                            type='primary'
                            onClick={handleCUpdChart}
                        >
                            Update Chart
                        </Button>
                    </Col>
                    <Col span={3}>
                        <Button
                            disabled={cSelectedChartID === -1}
                            icon={<DeleteIcon />}
                            style={{width: '95%', backgroundColor: 'red', float: 'right', marginBottom: '4px',}}
                            type='primary'
                            onClick={handleCDelChart}
                        >
                            Delete Chart
                        </Button>
                    </Col>
                    <Col span={4} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Form
                    form={formAUDCharts}
                    initialValues={{
                        cDetailsShowLegend: true,
                    }}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={1} />
                        <Col span={22}>
                            <div style={contentStyle}>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={22}>
                                        <Divider orientation='left' orientationMargin={50} variant='dotted' style={{ borderColor: 'black', color: getCActionColor() }}>
                                            {addButtonClicked ? 'Add Chart' :
                                                (updButtonClicked ? 'Update Chart: [' + cSelectedChartID + '] ' + cSelectedChartTitle :
                                                    (delButtonClicked ? 'Delete Chart: [' + cSelectedChartID + '] ' + cSelectedChartTitle : ''))}
                                        </Divider>
                                    </Col>
                                    <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Button
                                            style={{ color: getCActionColor(), fontStyle: 'italic' }}
                                            type='link'
                                            onClick={() => handleCChartResetForm(addButtonClicked ? 'blank' : 'selected')}
                                        >
                                            Reset Form
                                        </Button>
                                    </Col>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={7}>
                                        <Form.Item
                                            key='cdetailstitle'
                                            name='cDetailsTitle'
                                            label='Title'
                                            labelCol={{ span: 8, }}
                                            rules={[
                                                {
                                                    message: 'Title required.',
                                                    required: true,
                                                    validator: validateCDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Title'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={1} />
                                    <Col span={7}>
                                        <Form.Item
                                            key='cdetailstitlegeneric'
                                            name='cDetailsTitleGeneric'
                                            label='Title Generic'
                                            labelCol={{ span: 8, }}
                                            rules={[
                                                {
                                                    validator: validateCDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Title Generic'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={1} />
                                    <Col span={7}>
                                        <Form.Item
                                            key='cdetailsshowlegend'
                                            name='cDetailsShowLegend'
                                            rules={[{ validator: validateCDetailsData }]}
                                            valuePropName='checked'
                                            style={{
                                                marginBottom: '0px',
                                                marginTop: '0px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Checkbox disabled={delButtonClicked}>
                                                Show Legend
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={7}>
                                        <Form.Item
                                            key='cdetailsxaxistitle'
                                            name='cDetailsXAxisTitle'
                                            label='X Axis Title'
                                            labelCol={{ span: 8, }}
                                            rules={[
                                                {
                                                    validator: validateCDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter X Axis Title'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={1}/>
                                    <Col span={7}>
                                        <Form.Item
                                            key='cdetailyaxistitle'
                                            name='cDetailsYAxisTitle'
                                            label='Y Axis Title'
                                            labelCol={{ span: 8, }}
                                            rules={[
                                                {
                                                    validator: validateCDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Y Axis Title'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={1} />
                                    <Col span={7}>
                                        <Form.Item
                                            key='cdetaildatapointlabel'
                                            name='cDetailsDataPointLabel'
                                            label='Data Point Label'
                                            labelCol={{ span: 8, }}
                                            rules={[
                                                {
                                                    message: 'Data Point Label required.',
                                                    required: true,
                                                    validator: validateCDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Data Point Label'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={7}>
                                        <Form.Item
                                            key='cdetailsrefreshperiod'
                                            name='cDetailsRefreshPeriod'
                                            label='Refresh Period'
                                            labelCol={{ span: 8, }}
                                            rules={[
                                                {
                                                    message: 'Refresh Period required.',
                                                    required: true,
                                                    validator: validateCDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Refresh Period'
                                                style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '40%'}}
                                                type='number'
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={1} />
                                    <Col span={7}>
                                        <Form.Item
                                            key='cdetailsopacity'
                                            name='cDetailsOpacity'
                                            label='Opacity'
                                            labelCol={{ span: 8, }}
                                            rules={[
                                                {
                                                    validator: validateCDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Opacity'
                                                step='0.1'
                                                style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '40%'}}
                                                type='number'
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={9} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={14}>
                                        <Form.Item
                                            key='cdetailsdatasetid'
                                            name='cDetailsDataSetID'
                                            label='DataSet'
                                            labelCol={{ span: 4 }}
                                            rules={[
                                                {
                                                    message: 'DataSet required.',
                                                    required: true,
                                                    validator: validateCDetailsData,
                                                },
                                            ]}
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Select
                                                disabled={delButtonClicked}
                                                filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                                optionFilterProp='label'
                                                placeholder='Select DataSet'
                                                showSearch
                                                style={{ width: '100%' }}
                                            >
                                                {dataSets.map((eachDataSet) => (
                                                    <Option
                                                        key={eachDataSet.id}
                                                        value={eachDataSet.id}
                                                        label={eachDataSet.name}
                                                    >
                                                        {eachDataSet.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={10} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={1} />
                                    <Col span={22}>
                                        <div>
                                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                        </div>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '4px' }}>
                                    <Col span={4}>
                                        <Form.Item
                                            key='cdetailsdataset'
                                            name='cDetailsDataSet'
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Button
                                                style={{ width: '90%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                                type='primary'
                                                onClick={onCDataSet}
                                            >
                                                Chart DataSet
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                    <Col span={20} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row' style={{ visibility: addButtonClicked ? 'hidden' : 'visible' }}>
                                    <Col span={1} />
                                    <Col span={22}>
                                        <div>
                                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }} />
                                            <Text italic style={{ color: 'gray' }}>
                                                NOTE: Charts may be linked directly (via Foreign Key) to Chart DataSets, Drilldowns, Report Items, User Logs and more.
                                            </Text>
                                        </div>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={17} />
                        <Col span={3}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name='cApplyButton'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        htmlType='submit'
                                        style={{width: '95%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black'))}}
                                        type='primary'
                                    >
                                        {addButtonClicked ? 'Add Chart' : (updButtonClicked ? 'Update Chart' : (delButtonClicked ? 'Delete Chart' : ''))}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={3}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name='cCancelButton'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        style={{width: '95%'}}
                                        type='default'
                                        onClick={onCCancel}
                                    >
                                        {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                </Form>
            </Spin>
        </Card>
    );
};

export default AUDChart;
